import React from 'react';

import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import {
  CogIcon,
  OfficeBuildingIcon,
  ShieldCheckIcon
} from '@heroicons/react/outline';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceSection,
  PageHeader,
  WhoNeedsInsuranceSection,
  WhyIsInsuranceImportant
} from '../components';
import { BusinessPolicySvg } from '../components/svg';

const faqs = [
  {
    question: 'What is the difference between BOP and General Liability?',
    answer:
      'The main difference between with BOP and general liability is that BOP provides the same coverage as general liability with the added benefit of covering losses related to property and business interruption.'
  },
  {
    question: 'Is BOP required by law?',
    answer: `Insurance requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a BOP policy is crucial to protect your business from everyday risks.`
  },
  {
    question: 'How do I show proof of a BOP policy?',
    answer: `Once you’ve purchased your BOP policy, a Certificate of Insurance (“COI”) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance.`
  },
  {
    question: 'What is an additional insured? Can I add one to my BOP?',
    answer: `An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party with whom you conduct business.`
  }
];
const coverageItems = [
  {
    title: 'General Liability',
    description:
      "Protects against everyday workplace risks such as bodily injury, lawsuits and other third-party risks that arise when dealing with customers. You can see additional information on what general liability covers by clicking <a href='/general-liablity' class='text-primary hover:underline cursor-pointer font-medium'>here</a>.",
    icon: ShieldCheckIcon
  },
  {
    title: 'Commercial Property',
    description:
      'If your property, inventory or equipment is damaged or stolen, a BOP policy can cover costs of repairs or replacement.',
    icon: CogIcon
  },
  {
    title: 'Business Interruption',
    description:
      'In the event of a covered loss event, if your business cannot generate income, a BOP policy can pay for lost income and the costs associated with temporary relocation of your business.',
    icon: OfficeBuildingIcon
  }
];

const BusinessPolicy: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Business Owner's Policy"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: `Business Owner's Policy`
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is the difference between BOP and General Liability?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The main difference between BOP and general liability is that BOP provides the same coverage as general liability with the added benefit of covering losses related to property and business interruption."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is BOP required by law?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Insurance requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a BOP policy is crucial to protect your business from everyday risks."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I show proof of a BOP policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Once you've purchased your BOP policy, a Certificate of Insurance (COI) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is an additional insured? Can I add one to my BOP?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party with whom you conduct business."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <div className="overflow-hidden">
        <GradientBg />
        <Header />

        <PageHeader
          title="Business owner's policy"
          image={
            <>
              <BusinessPolicySvg className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0" />
              <img
                src="/img/sb5.jpg"
                className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
              />
            </>
          }
        />

        {/* Section-1 */}
        <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
              What is{' '}
              <span className="text-primary">business owner's policy</span>{' '}
              insurance?
            </h2>
          </FadeIn>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            Business owner's policy insurance, also known as BOP insurance, is a
            packaged insurance coverage which includes general liability,
            commercial property and business interruption insurance. It is the
            most common bundled policy to have in place to provide overall
            protection for any sized business, across all industries. Generally,
            the BOP policy will cost less due to its bundled nature when
            compared to separate purchases of general liability and commercial
            property policies.
          </p>
        </div>

        {/* Section-2 */}
        <CoverageSection
          title="business owner's policy"
          items={coverageItems}
        />

        {/* Section-3 */}
        <WhoNeedsInsuranceSection
          title="business owner's policy"
          description="Every small business, regardless of size or industry, needs protection from common business risks in the event of a lawsuit, third-party injury or property damage. If your business has valuable property and equipment, you should consider a business owner’s policy. A business owner’s policy provides the same coverage as a general liability policy with the added protection of covering costs associated with damages to your property, equipment, and inventory."
        />

        {/* Section-4 */}
        <WhyIsInsuranceImportant>
          <FadeIn>
            <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
              Why is a{' '}
              <span className="text-primary">business owner's policy</span>{' '}
              important?
            </h2>
          </FadeIn>

          <p className="mt-4 text-lg text-gray-500">
            A BOP policy includes general liability policy coverage, while also
            protecting your business property and potential lost income due to
            interruption of operations. Some landlords and building owners will
            require commercial occupants to retain business property insurance
            coverage which would be included under BOP. If you are legally
            required to have a general liability policy and would also like to
            protect your business property, a BOP policy may be the most
            appropriate coverage for your business.
          </p>

          <p className="mt-4 text-lg text-gray-500">
            Required or not, a business owner’s policy insurance guarantees that
            one big claim can’t derail you while you build and grow.
          </p>
        </WhyIsInsuranceImportant>

        <InsuranceSection />

        {/* Section-5 */}
        <FaqSection title="Business owner's policy Insurance" faqs={faqs} />

        <BottomGraphic />
        <Footer />
      </div>
    </>
  );
};

export default BusinessPolicy;
